import api from './api';
import { User, Permission } from './authService';

// Re-export Permission for use in other components
export type { Permission };

export interface Role {
  id: number;
  name: string;
  description?: string;
  permissions: Permission[];
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  role_id?: number;
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  is_active?: boolean;
  role_id?: number;
}

export interface CreateRoleRequest {
  name: string;
  description?: string;
  permission_ids?: number[];
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  permission_ids?: number[];
}

export interface UsersListParams {
  skip?: number;
  limit?: number;
  search?: string;
  role_id?: number;
  is_active?: boolean;
}

export const usersService = {
  // User Management
  async getUsers(params: UsersListParams = {}): Promise<User[]> {
    const response = await api.get<User[]>('/users/', { params });
    return response.data;
  },

  async getUser(userId: number): Promise<User> {
    const response = await api.get<User>(`/users/${userId}`);
    return response.data;
  },

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await api.post<User>('/users/', userData);
    return response.data;
  },

  async updateUser(userId: number, userData: UpdateUserRequest): Promise<User> {
    const response = await api.put<User>(`/users/${userId}`, userData);
    return response.data;
  },

  async deleteUser(userId: number): Promise<void> {
    await api.delete(`/users/${userId}`);
  },

  // Role Management
  async getRoles(): Promise<Role[]> {
    const response = await api.get<Role[]>('/users/roles/');
    return response.data;
  },

  async getRole(roleId: number): Promise<Role> {
    const response = await api.get<Role>(`/users/roles/${roleId}`);
    return response.data;
  },

  async createRole(roleData: CreateRoleRequest): Promise<Role> {
    const response = await api.post<Role>('/users/roles/', roleData);
    return response.data;
  },

  async updateRole(roleId: number, roleData: UpdateRoleRequest): Promise<Role> {
    const response = await api.put<Role>(`/users/roles/${roleId}`, roleData);
    return response.data;
  },

  async deleteRole(roleId: number): Promise<void> {
    await api.delete(`/users/roles/${roleId}`);
  },

  // Permission Management
  async getPermissions(): Promise<Permission[]> {
    const response = await api.get<Permission[]>('/users/permissions/');
    return response.data;
  },
};